import Router from "next/router";
import axios from "axios";
import store from "@app/lib/store";
import { toast } from "react-hot-toast";
import { APIURL } from "@app/config/apiUrl";
import { DOMAIN } from "@app/config/environment";
import { decryptData, encryptData } from "@app/utils/encrypt";
import { deleteCookie, getCookie, setCookie } from "@app/utils/cookie";
import { COOKIE_NAME, SALESFORCE_ERROR } from "@app/components/config";
import { hideLoader, showLoader } from "@app/lib/features/loader/loaderSlice";
import {
  removeUserDetails,
  setLoggedIn,
  setProfilePicture,
} from "@app/lib/features/login/loginSlice";

const encryptWithRSA = [
  // Auth
  `/${APIURL.AUTH}/${APIURL.LOGIN}`,
  `/${APIURL.AUTH}/${APIURL.OTP}/${APIURL.SEND}`,
  `/${APIURL.AUTH}/${APIURL.OTP}/${APIURL.VERIFY}`,
  `/${APIURL.AUTH}/${APIURL.FORGETPASSWORD}`,
  `/${APIURL.AUTH}/${APIURL.RESET_PASSWORD}`,
  // conatct-us
  `/${APIURL.CONTACT_US}/${APIURL.SUBMIT}`,
  // payment
  `/${APIURL.PAYMENT}`,
  // properties - two more apis are encrypted at call time
  `/${APIURL.PROPERTIES}/${APIURL.VIEWS}`,
  `/${APIURL.PROPERTIES}/${APIURL.GET_PROPERTY_HISTORY}`,
  `/${APIURL.PROPERTIES}/${APIURL.STATUS}/`,
  // salesforce
  `/${APIURL.SALESFORCE}/${APIURL.UPDATE_PROPERTY_STATUS}`,
  // user
  `/${APIURL.USER}/blockBuyer`,
  //save contact details
  `/${APIURL.USER}/${APIURL.SAVE_CONTACT}`
];

const formEncrypt = [`/${APIURL.AUTH}/${APIURL.REGISTER}`];

const encryptServiceWithRSA = [
  // enquiry - kept on whole moiddleware for now
  `/${APIURL.ENQUIRIES}`,
  // seller
  `/${APIURL.SELLER}/${APIURL.EDIT_PROPERTIES}/`,
  `/${APIURL.SELLER}/${APIURL.SERVICE_UPDATE}/`,
];

const axiosInstance: any = axios.create({
  baseURL: DOMAIN && `${DOMAIN}`,
});

axiosInstance.interceptors.request.use(async (config: any) => {
  if (typeof window !== "undefined") {
    const token = getCookie(COOKIE_NAME.token) || "";
    const refreshToken = getCookie(COOKIE_NAME.refreshToken) || "";
    if (token) config.headers["Authorization"] = `Bearer ${token}`;
    if (refreshToken) {
      config.headers["Refresh-Token"] = refreshToken;
    }
  }

  // Encrypt request data with RSA
  // Maybe add check for non-GET request
  if (
    (encryptWithRSA.includes(config.url) ||
      encryptServiceWithRSA.some((path) => config.url.includes(path))) &&
    config.data
  ) {
    config.data = {
      encryptedData: encryptData(JSON.stringify(config.data)),
    };
  } else if (formEncrypt.includes(config.url)) {
    let data = new FormData();
    config.data.forEach((value: any, key: any) => {
      if (
        key === "file" ||
        key === "profilePicture" ||
        key === "document" ||
        key === "passport" ||
        key === "emiratesId"
      ) {
        data?.append(key, value || "");
      } else {
        data?.append(key, encryptData(String(value)) || "");
      }
    });
    config.data = data;
  }

  if (!config.hideLoader) {
    store.dispatch(showLoader());
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (response: any) => {
    if (response?.headers?.Token) {
      setCookie(COOKIE_NAME.token, response.headers.Token);
    }
    if (!response.config.hideLoader) {
      store.dispatch(hideLoader());
    }
    if (response.data?.data?.hasOwnProperty("encryptedData")) {
      response.data.data = JSON.parse(
        decryptData(response.data.data.encryptedData)
      );
    }
    return response.data;
  },
  (error: any) => {
    if (
      (error?.response?.status === 401 &&
        String(error.response.data?.message).toLowerCase() ===
          "there is no user") ||
      (error?.response?.status === 500 &&
        String(error.response.data?.message).toLowerCase() ===
          "error authenticating user!" &&
        String(error.response.data?.error).toLowerCase() ===
          "tokens are expired!") ||
      error?.response?.status === 419
    ) {
      store.dispatch(setLoggedIn(false));
      store.dispatch(setProfilePicture(""));
      store.dispatch(removeUserDetails());
      sessionStorage.clear();
      localStorage.clear();
      deleteCookie(COOKIE_NAME.refreshToken);
      deleteCookie(COOKIE_NAME.token);
      deleteCookie(COOKIE_NAME.role);
      deleteCookie(COOKIE_NAME.isLoggedIn);
      deleteCookie(COOKIE_NAME.userDetails);

      if (typeof window !== "undefined") {
        toast.error("Session Expired, Kindly Re-login");
        setTimeout(() => {
          window.location.href = "/home";
        }, 1000);
      }
     
      store.dispatch(hideLoader());
      return;
    }
    store.dispatch(hideLoader());

    if (
      !error?.config?.hideError &&
      typeof window !== "undefined" &&
      error?.message != "Request cancelled due to new request."
    ) {
      toast.error(error?.response?.data?.message || "Something went wrong!");
    }
    console.error("error in api: ", error);
    throw error;
  }
);

export default axiosInstance;
